// src/context/AuthContext.js - FIXED for PWA reliability
import React, { createContext, useState, useContext, useEffect, useRef } from 'react';
import { authService } from '../services/authService';
import { profileService } from '../services/profileService';

const AuthContext = createContext();

const AUTH_TIMEOUT = 8000; // 8 seconds total timeout

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authChecked, setAuthChecked] = useState(false);
  const [error, setError] = useState(null);
  
  // Use ref to track if we're still mounted
  const isMounted = useRef(true);
  const timeoutRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    
    // Only check auth once on mount
    if (!authChecked) {
      initializeAuth();
    }
    
    return () => {
      isMounted.current = false;
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, [authChecked]);

  const initializeAuth = async () => {
    console.log('🔐 Starting auth initialization...');
    
    // CRITICAL FIX: Set up timeout protection that ALWAYS resolves
    timeoutRef.current = setTimeout(() => {
      if (isMounted.current && loading && !authChecked) {
        console.error('⏱️ Auth initialization FORCE TIMEOUT after 8s');
        setError('Authentication check timed out');
        setLoading(false);
        setAuthChecked(true);
        setToken(null);
        setUser(null);
      }
    }, AUTH_TIMEOUT);

    try {
      // Check authentication via backend (token in cookie)
      const isAuth = await Promise.race([
        authService.isAuthenticated(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('isAuthenticated timeout')), 6000)
        )
      ]);
      
      if (!isMounted.current) return; // Component unmounted
      
      if (isAuth) {
        console.log('✅ User is authenticated, fetching profile...');
        
        // Fetch basic user data from /users/profile/
        const userData = await Promise.race([
          authService.getProfile(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('getProfile timeout')), 4000)
          )
        ]);
        
        if (!isMounted.current) return;
        
        // Try to fetch full profile with bank accounts from /profile/profile/
        let fullProfile = null;
        try {
          fullProfile = await Promise.race([
            profileService.getUserProfile(),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('getUserProfile timeout')), 4000)
            )
          ]);
        } catch (profileError) {
          console.warn('⚠️ Could not fetch full profile, using basic user data:', profileError.message);
        }
        
        if (!isMounted.current) return;
        
        // Combine both sets of data
        const combinedUser = {
          ...userData,
          profile: fullProfile
        };
        
        setUser(combinedUser);
        setToken('authenticated'); // Just a flag, not actual token
        setError(null);
        console.log('✅ Auth initialized with combined data');
      } else {
        console.log('ℹ️ User not authenticated');
        setToken(null);
        setUser(null);
        setError(null);
      }
    } catch (error) {
      console.error('❌ Failed to initialize auth:', error.message);
      if (isMounted.current) {
        setError(error.message);
        setToken(null);
        setUser(null);
      }
    } finally {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (isMounted.current) {
        setLoading(false);
        setAuthChecked(true);
        console.log('✅ Auth initialization complete');
      }
    }
  };

  const login = async (email, password) => {
    console.log('🔓 Starting login...');
    
    try {
      const data = await Promise.race([
        authService.login({ email, password }),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Login timeout')), 10000)
        )
      ]);
      
      setToken('authenticated'); // Just a flag
      
      // Fetch basic user data
      const userData = await Promise.race([
        authService.getProfile(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile fetch timeout')), 5000)
        )
      ]);
      
      // Try to fetch full profile with bank accounts
      let fullProfile = null;
      try {
        fullProfile = await Promise.race([
          profileService.getUserProfile(),
          new Promise((_, reject) => 
            setTimeout(() => reject(new Error('Full profile timeout')), 5000)
          )
        ]);
      } catch (profileError) {
        console.warn('⚠️ Could not fetch full profile after login:', profileError.message);
      }
      
      // Combine both
      const combinedUser = {
        ...userData,
        profile: fullProfile
      };
      
      setUser(combinedUser);
      setError(null);
      
      console.log('✅ Login successful');
      return { success: true, data };
    } catch (error) {
      console.error('❌ Login failed:', error.message);
      setError(error.message);
      return { success: false, error: error.message };
    }
  };

  const register = async (userData) => {
    console.log('📝 Starting registration...');
    
    try {
      const data = await Promise.race([
        authService.register(userData),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Registration timeout')), 10000)
        )
      ]);
      
      console.log('✅ Registration successful');
      return { success: true, data };
    } catch (error) {
      console.error('❌ Registration failed:', error.message);
      return { success: false, error: error.message };
    }
  };

  const logout = async () => {
    console.log('🚪 Starting logout...');
    
    try {
      await Promise.race([
        authService.logout(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Logout timeout')), 5000)
        )
      ]);
      console.log('✅ Backend logout successful');
    } catch (error) {
      console.error('⚠️ Logout error:', error.message);
      // Continue with logout even if backend call fails
    } finally {
      // Clear state
      setToken(null);
      setUser(null);
      setAuthChecked(false);
      setError(null);
      
      // FORCE RELOAD to clear all state
      console.log('✅ Logout complete - reloading page');
      window.location.href = '/';
    }
  };

  const refreshProfile = async () => {
    console.log('🔄 Refreshing profile...');
    
    try {
      const fullProfile = await Promise.race([
        profileService.getUserProfile(),
        new Promise((_, reject) => 
          setTimeout(() => reject(new Error('Profile refresh timeout')), 5000)
        )
      ]);
      
      setUser(prev => ({
        ...prev,
        profile: fullProfile
      }));
      setError(null);
      console.log('✅ Profile refreshed successfully');
      return fullProfile;
    } catch (error) {
      console.error('❌ Failed to refresh profile:', error.message);
      setError(error.message);
      throw error;
    }
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loading,
    refreshProfile,
    isAuthenticated: !!token && !!user,
    error,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};